import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import type { ReactNode } from 'react';

export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SEPARATOR = /[\s,;]+/;

interface RecipientsFieldProps {
  chips: string[];
  draft: string;
  onChange: (chips: string[], draft: string) => void;
  max: number;
  label?: string;
  autoFocus?: boolean;
}

export function uniqueEmails(values: string[]): string[] {
  return [
    ...new Set(
      values.flatMap((value) => value.split(SEPARATOR)).map((email) => email.trim().toLowerCase()),
    ),
  ].filter((email) => email !== '');
}

// Text still in the input counts too, so a typed address needs no Enter before saving.
export function recipientsOf(chips: string[], draft: string): string[] {
  return uniqueEmails([...chips, draft]);
}

export function RecipientsField({
  chips,
  draft,
  onChange,
  max,
  label = 'Recipients',
  autoFocus = false,
}: RecipientsFieldProps): ReactNode {
  const recipients = recipientsOf(chips, draft);
  const invalid = recipients.filter((email) => !EMAIL.test(email));

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={chips}
      inputValue={draft}
      // Committing or clearing empties the input; removing a chip keeps what is being typed.
      onChange={(_, value, reason) =>
        onChange(uniqueEmails(value), reason === 'removeOption' ? draft : '')
      }
      onInputChange={(_, value) => {
        // A separator turns everything before it into chips.
        const parts = value.split(SEPARATOR);
        if (parts.length === 1) return onChange(chips, value);
        onChange(uniqueEmails([...chips, ...parts.slice(0, -1)]), parts.at(-1) ?? '');
      }}
      renderValue={(value, getItemProps) =>
        value.map((email, index) => (
          <Chip
            {...getItemProps({ index })}
            key={email}
            label={email}
            size="small"
            color={EMAIL.test(email) ? 'default' : 'error'}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          label={label}
          placeholder="name@company.com, press Enter"
          error={invalid.length > 0 || recipients.length > max}
          helperText={
            invalid.length > 0 ? `Not an email: ${invalid.join(', ')}` : `Up to ${max} people.`
          }
        />
      )}
    />
  );
}
